import { useContext, useState } from 'react'

import {
  useExternalArticleBulkApi,
  useExternalArticleDeleteApi,
  useExternalArticleRefetchAllApi,
  useExternalArticleRefetchApi,
  useExternalArticlesApi
} from '@/common/api/modules/externalArticle'
import {
  BulkResponse,
  ExternalArticle,
  ExternalArticleStatus
} from '@/common/api/params/externalArticle'
import { SearchConditionBase } from '@/common/api/search'
import { useSiteId } from '@/common/app/site'
import { useShowDeletedToast } from '@/common/app/toast'
import { WidgetChoice } from '@/common/form/choice'

import RateBar from '@/components/view/atoms/bar/RateBar'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import FormCaption from '@/components/view/atoms/form/FormCaption'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Textarea from '@/components/view/atoms/textarea/Textarea'
import TextField, { TextBoxSize } from '@/components/view/atoms/textfield/TextField'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import SearchConditionAreaContent from '@/components/view/layouts/area/SearchConditionAreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import DisclosureSortRadioSelect from '@/components/view/molecules/disclosure/DisclosureSortRadioSelect'
import PageListSearching from '@/components/view/molecules/loading/PageListSearching'
import NoResultGuidance from '@/components/view/molecules/search/NoResultGuidance'

import { DeleteDialogContext } from '@/components/container/modal/dialog/delete/DeleteDialogProvider'
import { ExternalArticleDetailModalContext } from '@/components/container/modal/externalArticle/ExternalArticleDetailModalProvider'
import ExternalArticleListTable from '@/components/container/modules/master/externalArticle/table/ExternalArticleListTable'

const STATUS_CHOICES: WidgetChoice<string>[] = [
  { name: '取り込み済み', value: ExternalArticleStatus.OK },
  { name: '取得待ち', value: ExternalArticleStatus.PENDING },
  { name: '取込除外', value: ExternalArticleStatus.EXCLUDED },
  { name: '取得不可', value: ExternalArticleStatus.MISSING }
]

interface Condition extends SearchConditionBase {
  status: string | null
  host: string | null
  q: string
}

const PER_PAGE = 30
const CONDITION_CLEAR: Condition = { page: 1, perPage: PER_PAGE, status: null, host: null, q: '' }
// 取得はサーバ側のワーカーが5分おきに動く。取得待ちが残っている間だけ一覧を追従させる
const PENDING_REFRESH_MS = 15000

const SummaryCount = ({ label, value, sub }: { label: string; value: number; sub?: string }) => (
  <SummaryItem>
    <ItemAlign gapX={8} align='center'>
      <Text size={12} weight='bold'>
        {label}
      </Text>
      {sub && (
        <Text size={10} color={TextColor.SECONDARY}>
          {sub}
        </Text>
      )}
    </ItemAlign>
    <Text size={24} weight='bold'>
      {value.toLocaleString()}
    </Text>
  </SummaryItem>
)

const ExternalArticleListSection = () => {
  const siteId = useSiteId()
  const showDeleted = useShowDeletedToast()
  const { showModal: showDeleteDialog, hide: hideDeleteDialog } = useContext(DeleteDialogContext)
  const [condition, setCondition] = useState<Condition>(CONDITION_CLEAR)
  const [qInput, setQInput] = useState('')
  const { showModal: showDetailModal } = useContext(ExternalArticleDetailModalContext)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkText, setBulkText] = useState('')
  const [bulkResult, setBulkResult] = useState<BulkResponse | null>(null)
  // 行ごとの操作は「実行中の対象ID」を持つ。フック単位の isMutating を当てると
  // 1行押しただけで全行のボタンが反応する
  const [refetchingId, setRefetchingId] = useState<number | null>(null)

  const [pendingSeen, setPendingSeen] = useState(false)
  const { data, isLoading, mutate } = useExternalArticlesApi(
    {
      site_id: siteId,
      limit: PER_PAGE,
      offset: (condition.page - 1) * PER_PAGE,
      ...(condition.status && { status: condition.status as ExternalArticleStatus }),
      ...(condition.host && { host: condition.host }),
      ...(condition.q && { q: condition.q })
    },
    pendingSeen ? PENDING_REFRESH_MS : undefined
  )

  // 取得待ちが無くなったら自動更新を止める（画面を開きっぱなしでも無駄に叩かない）
  if (data && data.pending > 0 !== pendingSeen) setPendingSeen(data.pending > 0)

  const { trigger: bulk, isMutating: bulkRunning } = useExternalArticleBulkApi((r) => {
    setBulkResult(r)
    setBulkText('')
    mutate()
  })
  const { trigger: refetch } = useExternalArticleRefetchApi(
    () => {
      setRefetchingId(null)
      mutate()
    },
    () => setRefetchingId(null)
  )
  const { trigger: refetchAll, isMutating: refetchAllRunning } = useExternalArticleRefetchAllApi(
    () => mutate()
  )
  const { trigger: remove } = useExternalArticleDeleteApi(() => {
    hideDeleteDialog()
    mutate()
  })

  const onClickDelete = (article: ExternalArticle) => {
    const name = article.title || article.article_id
    showDeleteDialog({
      name,
      onClickDelete: () =>
        remove({ site_id: siteId, article_pk: article.id }).then(() => showDeleted(name))
    })
  }

  const rows = data?.items ?? []
  const counts = data?.counts ?? {}
  const total = data?.total ?? 0
  const totalAll = Object.values(counts).reduce((a, b) => a + b, 0)
  const fetched = totalAll - (counts[ExternalArticleStatus.PENDING] ?? 0)
  const isDefault = condition.status === null && condition.host === null && condition.q === ''
  const hostChoices: WidgetChoice<string>[] = (data?.hosts ?? []).map((h) => ({
    name: h,
    value: h
  }))

  const resetCondition = () => {
    setQInput('')
    setCondition(CONDITION_CLEAR)
  }

  return (
    <Content>
      <FormCaption size={12}>
        記事ページが閲覧されると自動で取り込まれます。取り込んだ記事は特集ページと同じ構造で保存され、
        Media-Driver の分析・記事案、記事レコメンド、Findee、AIタグの対象になります。
        ページの取得は数分おきのバックグラウンド処理で行うため、取得待ちの記事は少し待つと状態が変わります。
      </FormCaption>
      <Area>
        <SummaryItemList>
          <SummaryCount label='登録件数' value={totalAll} />
          <SummaryCount
            label='取り込み済み'
            value={counts[ExternalArticleStatus.OK] ?? 0}
            sub='Media-Driver・記事レコメンドの対象'
          />
          <SummaryCount label='取込除外' value={counts[ExternalArticleStatus.EXCLUDED] ?? 0} />
          <SummaryCount label='取得不可' value={counts[ExternalArticleStatus.MISSING] ?? 0} />
        </SummaryItemList>
      </Area>

      {/* 取得の進捗。取得待ちがある間だけ出す */}
      {data && data.pending > 0 && (
        <Area>
          <Heading
            subText={`${fetched.toLocaleString()} / ${totalAll.toLocaleString()} 件（残り ${data.pending.toLocaleString()} 件）`}
          >
            取得中
          </Heading>
          <AreaContent>
            <RateBar percent={totalAll ? Math.round((fetched / totalAll) * 100) : 0} />
            <FormCaption size={12}>
              ページの取得は数分おきに数十件ずつ進みます。この画面は自動で更新されます。
            </FormCaption>
          </AreaContent>
        </Area>
      )}

      {/* 一括投入 */}
      {bulkOpen && (
        <Area>
          <Heading>記事URLの一括投入</Heading>
          <AreaContent>
            <FormCaption size={12}>
              記事のURLを改行で並べて貼り付けます。対象URLパターンに一致しない行は受け付けず、
              理由を返します。ページの取得はこのあとバックグラウンドで進みます。
            </FormCaption>
            <Textarea
              name='bulk_urls'
              value={bulkText}
              minRows={6}
              maxLength={5000}
              placeholder={
                'https://example.com/Page/column/c26081901.aspx\nhttps://example.com/Page/column/c26081902.aspx'
              }
              onChange={(e) => setBulkText(e.target.value)}
            />
            <ItemAlign>
              <Button
                loading={bulkRunning}
                disabled={bulkRunning || !bulkText.trim()}
                onClick={() => bulk({ site_id: siteId, urls: bulkText })}
              >
                投入する
              </Button>
            </ItemAlign>
            {bulkResult && (
              <ItemAlign direction='vertical' gapY={4}>
                <Text size={12}>
                  受付 {bulkResult.accepted} 件 / 重複 {bulkResult.duplicated} 件 / 対象外{' '}
                  {bulkResult.rejected} 件
                </Text>
                {bulkResult.rejected_items.map((r) => (
                  <Text key={r.url} size={12} color={TextColor.SECONDARY}>
                    {r.url} … {r.reason}
                  </Text>
                ))}
              </ItemAlign>
            )}
          </AreaContent>
        </Area>
      )}

      <Area tableWrap noBorder>
        <SearchConditionAreaContent>
          <div className='flex w-full items-center justify-between gap-2'>
            <ItemAlign gapX={8} align='center' nowrap full>
              <DisclosureSortRadioSelect
                name='status'
                currentValue={condition.status}
                choices={STATUS_CHOICES}
                setCondition={setCondition}
              >
                状態
              </DisclosureSortRadioSelect>
              {hostChoices.length > 1 && (
                <DisclosureSortRadioSelect
                  name='host'
                  currentValue={condition.host}
                  choices={hostChoices}
                  setCondition={setCondition}
                >
                  取得元
                </DisclosureSortRadioSelect>
              )}
              <Button color={ButtonColor.SECONDARY} disabled={isDefault} onClick={resetCondition}>
                条件をリセット
              </Button>
            </ItemAlign>
            <ItemAlign gapX={8} align='center' nowrap full>
              <TextField
                type='search'
                size={TextBoxSize.LARGE}
                value={qInput}
                placeholder='タイトル・記事ID・URLで検索する'
                onChange={(e) => setQInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setCondition((c) => ({ ...c, page: 1, q: qInput.trim() }))
                  }
                }}
              />
              <Button color={ButtonColor.SECONDARY} onClick={() => setBulkOpen((v) => !v)} shrink0>
                URLをまとめて投入
              </Button>
              <Button
                color={ButtonColor.SECONDARY}
                loading={refetchAllRunning}
                disabled={refetchAllRunning || totalAll === 0}
                onClick={() => refetchAll({ site_id: siteId })}
                shrink0
              >
                全件を取り直す
              </Button>
            </ItemAlign>
          </div>
        </SearchConditionAreaContent>

        {isLoading ? (
          <PageListSearching />
        ) : rows.length === 0 ? (
          isDefault ? (
            <AreaContent>
              <Text color={TextColor.SECONDARY}>
                外部記事はまだありません。取り込み設定タブで対象URLパターンを登録すると、
                記事ページが閲覧された時点で取り込まれます。
              </Text>
            </AreaContent>
          ) : (
            <NoResultGuidance searched name='記事' onClickReset={resetCondition} />
          )
        ) : (
          <ExternalArticleListTable
            articles={rows}
            total={total}
            condition={condition}
            onUpdatePage={(page) => setCondition((c) => ({ ...c, page }))}
            refetchingId={refetchingId}
            onClickRow={(r) => showDetailModal({ siteId, articlePk: r.id })}
            onRefetch={(r) => {
              setRefetchingId(r.id)
              refetch({ site_id: siteId, article_pk: r.id })
            }}
            onDelete={onClickDelete}
          />
        )}
      </Area>
    </Content>
  )
}

export default ExternalArticleListSection
